import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, ChevronDown, HelpCircle, Phone, Mail } from 'lucide-react'
import { scrapedData } from '../data'
import { renderFormattedText } from '../utils/textFormatter'
import SEO from '../components/SEO'

export default function FaqPage() {
  const { sporgOgSvar } = scrapedData
  const [openFaqId, setOpenFaqId] = useState(null)
  const [activeCategory, setActiveCategory] = useState('Alle')

  const toggleFaq = (idx) => {
    setOpenFaqId(openFaqId === idx ? null : idx)
  }

  const categories = ['Alle', 'Spædbørn', 'Børn & Voksne']

  const filteredFaqs = activeCategory === 'Alle'
    ? sporgOgSvar.faqs
    : sporgOgSvar.faqs.filter((faq) => faq.category === activeCategory)

  return (
    <div className="space-y-20 pb-16">
      <SEO
        title="Ofte stillede spørgsmål (FAQ) | Rørbækklinikken Viborg"
        description="Find svar på ofte stillede spørgsmål om vores behandlinger, bihulebetændelse, spædbørnsuro, kolik og forløb hos Rørbækklinikken i Viborg."
      />
      
      {/* Page Header */}
      <section className="bg-[#F5F0E6]/40 py-16 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E352F]">
            Spørgsmål og Svar – Rørbækklinikken
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/80 leading-relaxed">
            Velkommen til vores brevkasse. Her kan du læse svar på spørgsmål fra vores klienter om forskellige lidelser, behandlingsforløb og gode råd.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Category Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 border-b border-[#E6DFD3] pb-6">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setActiveCategory(cat)
                setOpenFaqId(null)
              }}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                activeCategory === cat
                  ? 'bg-[#1E352F] text-white shadow-sm'
                  : 'bg-[#F5F0E6]/60 text-[#1E352F]/80 hover:bg-[#F5F0E6] hover:text-[#1E352F]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Accordion List */}
        <div className="space-y-4">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 text-[#1E352F]/60">
              Ingen spørgsmål fundet i denne kategori.
            </div>
          ) : (
            filteredFaqs.map((faq, idx) => {
              const isOpen = openFaqId === idx
              return (
                <div
                  key={idx}
                  className="bg-white rounded-xl border border-[#E6DFD3] overflow-hidden shadow-sm transition-all"
                >
                  {/* Question Header */}
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full px-6 py-5 text-left flex justify-between items-center gap-4 hover:bg-[#FDFBF7]/50 transition-colors cursor-pointer"
                  >
                    <span className="font-serif text-base sm:text-lg font-bold text-[#1E352F]">
                      {faq.question}
                    </span>
                    <span className="text-[#eebb53] shrink-0">
                      <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </span>
                  </button>

                  {/* Answer Content */}
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100 border-t border-[#F5F0E6]' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 pt-4 bg-[#FDFBF7]/30">
                        <div className="text-sm sm:text-base text-[#1E352F]/80 leading-relaxed space-y-4">
                          {renderFormattedText(faq.content, "font-serif text-xl font-bold text-[#1E352F] pt-4 pb-1")}
                        </div>
                        <div className="mt-4 pt-4 border-t border-[#F5F0E6] flex justify-between items-center text-[10px] sm:text-xs text-[#1E352F]/50 uppercase tracking-wider font-semibold">
                          <span>Kategori: {faq.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </section>

      {/* Direct Help Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#F5F0E6] rounded-2xl p-6 sm:p-8 border border-[#E6DFD3] flex flex-col sm:flex-row gap-6 items-start">
          <div className="p-4 rounded-xl bg-white text-[#eebb53] shrink-0">
            <HelpCircle className="w-8 h-8" />
          </div>
          <div className="space-y-3">
            <h3 className="font-serif text-xl font-bold text-[#1E352F]">
              Fandt du ikke svar på dit spørgsmål?
            </h3>
            <p className="text-sm text-[#1E352F]/80 leading-relaxed">
              Du er altid mere end velkommen til at kontakte os direkte. Vi sidder klar til at besvare dine spørgsmål om lidelser, behandlinger eller særlige behov.
            </p>
            <div className="pt-2 flex flex-wrap gap-4 text-xs sm:text-sm text-[#1E352F]/80">
              <span className="flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-[#eebb53]" />
                Tlf: <a href="tel:86639393" className="font-bold hover:underline">86 63 93 93</a>
              </span>
              <span className="flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-[#eebb53]" />
                Email: <a href="mailto:im@muskelterapi.com" className="font-bold hover:underline">im@muskelterapi.com</a>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-2xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43]">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white">
            Klar til at bestille din behandling?
          </h2>
          <p className="text-sm sm:text-base text-[#F5F0E6]/80 max-w-2xl mx-auto leading-relaxed">
            Vælg din ønskede behandling og book direkte i vores online bookingsystem Planway. Det tager under 2 minutter.
          </p>
          <div className="pt-2">
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-sm"
            >
              <Calendar className="w-5 h-5" />
              Book tid online
            </a>
          </div>
        </div>
      </section>

    </div>
  )
}
